import { memo, useState, useCallback } from "react";
import { Button } from "../../common/button/button";
import { sanitizeImageUrl } from "../../../utils/sanity.utils";
import { Bootcamp } from "../../../model/bootcamp";
import { getCourseStartDate } from "../../../utils/date.utils";
import { RoundIcon } from "./assets/round.icon";
import { PopUpBlock } from "../../common/pop-up/pop-up";
import { useRouter } from "next/router";
import { InfoAboutBootcamp } from "../../../model/info-about-bootcamp";
import { FormCmsData } from "../../../model/form";
import { getCourseStatus } from "../../../utils/course-utils";

function getBootcampDuration(
  isOptimism: boolean,
  isBlast: boolean,
  infoAboutBootcamp: { duration: string }
): string {
  if (isBlast) return "Free";
  if (isOptimism) return "2 months";
  return infoAboutBootcamp.duration;
}

interface Props {
  info: Bootcamp;
  ctaTitle: string;
  startTitle: string;
  bootcampTitle: string;
  infoAboutBootcamp: InfoAboutBootcamp;
  orTitle: string;
  forms: FormCmsData;
  bootcampName?: string;
}

export const Intro = memo<Props>(
  ({
    info,
    ctaTitle,
    startTitle,
    bootcampTitle,
    infoAboutBootcamp,
    orTitle,
    forms,
  }) => {
    const { locale } = useRouter();
    const [isContactSubmitPopupShown, setIsContactSubmitPopupShown] =
      useState<boolean>(false);

    const onContactPopupSubmitted = useCallback(() => {
      setIsContactSubmitPopupShown(false);
    }, []);

    const todayDate = new Date();
    const { isOptimism, isBlast, isFreeCourses } = getCourseStatus(info._id);

    const handleClick = () => {
      if (isFreeCourses) {
        const priceElement = document.querySelector("#price");
        if (!priceElement) return;

        window.scrollTo({
          top: priceElement.getBoundingClientRect().top + window.scrollY,
          behavior: "smooth",
        });
      } else {
        setIsContactSubmitPopupShown(true);
      }
    };

    return (
      <>
        <div
          className={
            isContactSubmitPopupShown ? "block cursor-pointer" : "hidden"
          }
        >
          <PopUpBlock
            formId={"Попап с внутренней страницы / интро"}
            bootcampName={info.title}
            orTitle={orTitle}
            forms={forms}
            onContactPopupSubmitted={onContactPopupSubmitted}
          />
        </div>
        <div className="text-white">
          <div className="rounded-[20px] bg-night-background pb-10 p-4 lg:p-8">
            <div className="relative lg:flex">
              <div className="grow-0 basis-6/12">
                <video
                  autoPlay
                  loop
                  controls={false}
                  muted
                  className="w-full rounded-[16px]"
                  poster={sanitizeImageUrl(info.image.asset._ref)}
                >
                  <source
                    src={`https://website-videos-for-bootcamps.s3.eu-central-1.amazonaws.com/videos/${info.slug}.mp4`}
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>

              <div className="flex grow-0 basis-6/12 flex-col justify-center pl-4 pr-4 lg:pr-0 lg:pl-16">
                <div className="pt-10 pb-10 lg:pb-10 lg:pt-0 lg:absolute lg:top-[0px] flex">
                  <div className="rounded-[38px] bg-white-opacity-008 pt-2 pb-2 pl-6 pr-6 lg:pt-2 lg:pb-2 lg:pl-8 lg:pr-8 max-w-fit mr-3 text-[16px] leading-[130%] flex items-center">
                    <RoundIcon className="mr-3" />
                    {isBlast ? "" : startTitle}{" "}
                    {getCourseStartDate(
                      isOptimism,
                      isBlast,
                      "Limited offer",
                      todayDate,
                      info.ranking,
                      locale
                    )}
                  </div>
                  <div className="rounded-[38px] bg-white-opacity-008 pt-2 pb-2 pl-6 pr-6 lg:pt-2 lg:pb-2 lg:pl-8 lg:pr-8 max-w-fit text-[16px] leading-[130%]">
                    {getBootcampDuration(
                      isOptimism,
                      isBlast,
                      infoAboutBootcamp
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-night-base-base-500 pb-6 lg:pb-11 text-[36px] leading-[36px] lg:text-[48px] uppercase lg:leading-[46px]">
                    {bootcampTitle}{" "}
                    <div className="text-white">{info.title}</div>
                  </div>

                  <div className="pb-12 lg:pb-17 text-[16px] leading-[130%] lg:text-[18px] text-night-base-base-500">
                    {info.intro}
                  </div>
                  <Button
                    onClick={handleClick}
                    size="m"
                    theme="accent-blue"
                    className="w-full lg:max-w-max"
                  >
                    <span className="lg:relative top-[-2px]">{ctaTitle}</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
);
